import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { DialogWrapper } from './DialogWrapper';
import { colors } from '../../../styles/theme/colors';
import { FONT_FAMILY } from '../../../styles/theme/theme';
import { useResponsive } from '../../../utils/responsive';
import { UserRole } from '@/types/user';

interface BaseDialogAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'outlined';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

interface BaseDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  icon?: string | React.ReactNode;
  actions?: BaseDialogAction[];
  maxWidth?: string;
  showCloseButton?: boolean;
  backdropOpacity?: 'light' | 'default' | 'dark';
  userRole?: `${UserRole}`;
  customStyles?: React.CSSProperties;
  innerContentStyle?: React.CSSProperties;
  className?: string;
}

export const BaseDialog: React.FC<BaseDialogProps> = ({
  open,
  onClose,
  title,
  description,
  children,
  icon,
  actions,
  maxWidth = '492px',
  showCloseButton = true,
  backdropOpacity = 'default',
  userRole,
  customStyles,
  innerContentStyle,
  className,
}) => {
  const { isMobile } = useResponsive();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const root = document.getElementById('root');
    if (root) {
      root.setAttribute('aria-hidden', 'true');
    }

    if (dialogRef.current) {
      dialogRef.current.focus();
    }

    return () => {
      if (root) {
        root.removeAttribute('aria-hidden');
      }
    };
  }, [open]);

  const getPrimaryColor = () => {
    if (!userRole) return colors.sender.primary;
    return userRole === `${UserRole.SENDER}` ? colors.sender.primary : colors.traveller.primary;
  };

  const primaryColor = getPrimaryColor();

  const getButtonStyles = (variant: BaseDialogAction['variant'] = 'primary', disabled?: boolean) => {
    const baseStyles: React.CSSProperties = {
      fontFamily: FONT_FAMILY,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '21px',
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      opacity: disabled ? 0.6 : 1,
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: disabled ? colors.button.disabled : primaryColor,
          color: colors.neutral.white,
        };
      case 'outlined':
        return {
          ...baseStyles,
          backgroundColor: colors.neutral.white,
          color: colors.text.primary,
          border: `1px solid ${colors.border.default}`,
        };
      case 'secondary':
      default:
        return {
          ...baseStyles,
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
        };
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      backdropOpacity={backdropOpacity}
      maxWidth={false}
      PaperProps={{
        ref: dialogRef,
        tabIndex: -1,
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': title ? 'base-dialog-title' : undefined,
        sx: {
          width: { xs: '90%', sm: maxWidth },
          maxWidth: maxWidth,
          maxHeight: { xs: '90dvh', sm: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          background: colors.neutral.white,
          borderRadius: '16px',
          boxShadow: '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)',
          padding: 0,
          margin: 0,
          position: 'relative',
          ...customStyles,
        },
        className,
      }}
    >
      <Box
        sx={{
          padding: '32px 24px',
          position: 'relative',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#D9DADF',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#888',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#D9DADF transparent',
          ...innerContentStyle,
        }}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background 0.2s',
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.background.paper;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            aria-label="Close dialog"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke={colors.payoutBanner.closeColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {icon && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
            }}
          >
            {typeof icon === 'string' ? (
              <img src={icon} alt="" style={{ width: '80px', height: '80px' }} />
            ) : (
              icon
            )}
          </div>
        )}

        {title && (
          <h2
            id="base-dialog-title"
            style={{
              fontFamily: FONT_FAMILY,
              fontWeight: 500,
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: isMobile ? '24px' : '27px',
              color: colors.form.label,
              textAlign: 'center',
              margin: '0 0 8px 0',
            }}
          >
            {title}
          </h2>
        )}

        {description && (
          <p
            style={{
              fontFamily: FONT_FAMILY,
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '21px',
              color: colors.form.input,
              textAlign: 'center',
              margin: '0 0 24px 0',
            }}
          >
            {description}
          </p>
        )}

        {children && <div>{children}</div>}

        {actions && actions.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile && actions.length > 1 ? 'column' : 'row',
              gap: '12px',
              justifyContent: 'center',
            }}
          >
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                style={{
                  ...getButtonStyles(action.variant, action.disabled || action.loading),
                  width: action.fullWidth ? '100%' : isMobile ? '100%' : 'auto',
                  minWidth: !isMobile && !action.fullWidth ? '120px' : undefined,
                }}
              >
                {action.loading ? '...' : action.label}
              </button>
            ))}
          </div>
        )}
      </Box>
    </DialogWrapper>
  );
};
